import { Globals } from "./Globals";
import { kAdminUser, kRWUser, kROUser, enumFeaturesList, enumFolderStatus } from "../constants";

/**
 * Single source of truth for role / feature / UI-profile permission checks.
 *
 * Instantiated on wsMgr (`wsMgr.fPermissions`) because permissions derive
 * from multiple pieces of wsMgr-owned state: user role (fUserInfo), doc
 * state (GetDoc), access groups (GetCurrentUserGroup), plan manager,
 * file manager, org info. Co-locating them fixes the latent tech debt of
 * reading user role via two paths (Globals._accountInfo.GetUserRole() vs
 * wsMgr.fUserInfo.GetUserRole() — different QAccountInfo/QUser instances
 * holding the same fUserType, which could diverge). Role now reads via
 * this.userRole → fWSMgr.fUserInfo only.
 *
 * UI-profile config (Globals._uiConfig.*) and feature flags
 * (Globals._accountInfo.GetAccessibleFeature) stay on their Globals
 * singletons — those are config, not duplicated state, and there's no
 * dual-path problem.
 *
 * 1:1 port of `UI/src/hooks/usePermissionCheck.js` — same names, same
 * logic. A follow-up will replace usePermissionCheck with a thin wrapper
 * that returns `wsMgr.fPermissions`.
 */
export class QPermissions {
  constructor(wsMgr) {
    this.fWSMgr = wsMgr;
  }

  // ---- accessors for common reads (used by the methods below) ----

  get userInfo() {
    return this.fWSMgr?.GetUserInfo?.();
  }

  get userRole() {
    return this.userInfo?.GetUserRole?.();
  }

  get doc() {
    return this.fWSMgr?.GetDoc?.();
  }

  get userGroup() {
    return this.userInfo && this.fWSMgr?.GetCurrentUserGroup?.();
  }

  get fileMgr() {
    return this.fWSMgr?.GetFileMgr?.();
  }

  /** Thin wrapper around Globals._accountInfo.GetAccessibleFeature —
   *  kept as a method so callers don't duplicate the null-safety chain. */
  hasFeature(feature) {
    return Globals._accountInfo?.GetAccessibleFeature?.(feature);
  }

  showFunctionalStatement() {
    return !!Globals._uiConfig?.fFunctionalStatement;
  }

  showChartNavigator() {
    return !!Globals._uiConfig?.fChartNavigator;
  }

  showSidebarFilters() {
    return Globals._uiConfig?.fSidebarFilters !== false;
  }

  showSidebarHighlights() {
    return Globals._uiConfig?.fSidebarHighlights !== false;
  }

  showSidebarBackground() {
    return Globals._uiConfig?.fSidebarBackground !== false;
  }

  showSidebarEmployeeList() {
    return Globals._uiConfig?.fSidebarEmployeeList !== false;
  }

  // ---- role predicates ----

  isAdmin() {
    return this.userRole === kAdminUser;
  }

  isReadOnly() {
    return this.userRole === kROUser;
  }

  isFullAccess() {
    return this.userInfo?.GetUserGroupID?.() == "0";
  }

  isSupportLogin() {
    return this.fWSMgr?.fAdminLogin;
  }

  canEditDocuments() {
    const role = this.userRole;
    return role === kAdminUser || role === kRWUser;
  }

  canDeleteAllFolderContents() {
    return this.isSupportLogin() && this.userRole === kAdminUser;
  }

  canViewRecordSourceDetails() {
    return this.userRole === kAdminUser;
  }

  canAccessChartSettings() {
    return this.userRole === kAdminUser;
  }

  // ---- mode access ----

  canAccessEdit() {
    return (
      Globals._uiConfig.fHasWorkbench &&
      this.canEditDocuments() &&
      this.hasFeature(enumFeaturesList.customChart)
    );
  }

  canAccessPIT() {
    return this.fWSMgr && this.userInfo && this.userInfo.GetCanPIT() && this.canEditDocuments();
  }

  canAccessPlanning() {
    return (
      this.fWSMgr &&
      Globals._uiConfig.fHasPlanning &&
      this.fWSMgr.GetPlanningStatus() &&
      this.canEditDocuments()
    );
  }

  canAccessReporting() {
    return (
      this.fWSMgr &&
      Globals._uiConfig.fHasReporting &&
      this.hasFeature(enumFeaturesList.reporting) &&
      this.fWSMgr.GetReportingStatus() &&
      this.userRole !== kROUser &&
      (this.userInfo.GetCanReport() || [kAdminUser].includes(this.userRole))
    );
  }

  canAccessDirectory() {
    return (
      this.fWSMgr && Globals._uiConfig.fHasDirectory && this.hasFeature(enumFeaturesList.directory)
    );
  }

  canAccessPosMgmt() {
    return this.fWSMgr && Globals._uiConfig.GetPositionMgmtEnabled();
  }

  // ---- filter / highlight ----

  hasIFH() {
    return this.userInfo && this.userInfo.HasIFH();
  }

  canAccessSavedFilters() {
    return this.hasFeature(enumFeaturesList.savedFilters);
  }

  canAccessSavedHighlights() {
    return this.hasFeature(enumFeaturesList.savedHighlights);
  }

  canAccessInteractiveFilters() {
    return this.hasIFH() && this.hasFeature(enumFeaturesList.interactiveFilters);
  }

  canAccessInteractiveHighlights() {
    return this.hasIFH() && this.hasFeature(enumFeaturesList.interactiveHighlights);
  }

  shouldShowUpgradeFilters() {
    return (
      this.showPricingAndPackaging() &&
      (this.hasIFH()
        ? !this.canAccessInteractiveFilters() && !this.canAccessSavedFilters()
        : !this.canAccessSavedFilters())
    );
  }

  shouldShowUpgradeHighlights() {
    return (
      this.showPricingAndPackaging() &&
      (this.hasIFH()
        ? !this.canAccessInteractiveHighlights() && !this.canAccessSavedHighlights()
        : !this.canAccessSavedHighlights())
    );
  }

  // ---- templates / views ----

  canAccessTemplates() {
    return this.userInfo && this.userInfo.CanUpdateTemplates();
  }

  /**
   * LENIENT view access check — answers "should the URL gate strip
   * `?view=`?". Used by `useUrlSecurityGate` where false-positive strips
   * are destructive (once stripped, the param cannot be re-added if a
   * later chartDocument update would have allowed it). Defers on any
   * ambiguity; only strips when confidence is high.
   *
   * Return states:
   *   - viewId null/empty → true (nothing to restrict).
   *   - viewMgr missing → true (defer, doc not ready).
   *   - viewId not found by `GetViewByID` → true (defer — could be
   *     wrong-chart's fViewList during a cross-chart transition, or a
   *     legacy name-form identifier that coreapp resolves downstream).
   *   - viewId found AND in `GetAllowedViewList` → true (allow).
   *   - viewId found AND NOT in allowed list → false (strip — this is
   *     access-group restriction, an unambiguous deny).
   *
   * For the stricter "can we APPLY this view now?" check used at the
   * resource-loader layer (ViewManagerProvider), use `canApplyView`.
   */
  canAccessView(viewId) {
    if (viewId == null || viewId === "") return true;
    const viewMgr = this.doc?.GetViewMgr?.();
    if (!viewMgr) return true;
    const viewExistsForChart = viewMgr.GetViewByID?.(viewId) != null;
    if (!viewExistsForChart) return true;
    const allowed = viewMgr.GetAllowedViewList?.() ?? [];
    return allowed.map(String).includes(String(viewId));
  }

  /**
   * STRICT view-application check — answers "can we call
   * `DoSetView(viewId)` right now?". Used by `ViewManagerProvider` where
   * the effect re-fires on chartDocument changes, so a false-negative
   * first pass is recoverable (second pass after template-load will
   * allow). Denies when the view is not currently in fViewList
   * (unpromoted or not loaded yet), and when access-group excludes it.
   *
   * Return states:
   *   - viewId null/empty → true (nothing to apply anyway).
   *   - viewMgr missing OR numViews === 0 → false (defer application
   *     until templates load; effect re-fires on chartDocument change).
   *   - viewId not in fViewList → false (unpromoted to this chart, or
   *     stale numViews from prior chart — either way, don't apply).
   *   - viewId in fViewList but not in allowed list → false.
   *   - viewId passes both → true.
   */
  canApplyView(viewId) {
    if (viewId == null || viewId === "") return true;
    const viewMgr = this.doc?.GetViewMgr?.();
    if (!viewMgr) return false;
    const numViews = viewMgr.GetNumViews?.() ?? 0;
    if (numViews === 0) return false;
    const viewExistsForChart = viewMgr.GetViewByID?.(viewId) != null;
    if (!viewExistsForChart) return false;
    const allowed = viewMgr.GetAllowedViewList?.() ?? [];
    return allowed.map(String).includes(String(viewId));
  }

  canAccessViewLibrary() {
    return (
      this.canAccessTemplates() &&
      (this.hasFeature(enumFeaturesList.promoteViews) || this.hasFeature(enumFeaturesList.editView))
    );
  }

  canAccessViewSwitcher() {
    return this.hasFeature(enumFeaturesList.viewSwitcher);
  }

  showViews() {
    return this.canAccessViewSwitcher() && !!Globals._uiConfig?.fHasViews;
  }

  canAccessBoxLibrary() {
    return this.canAccessTemplates() && this.hasFeature(enumFeaturesList.boxStyles);
  }

  // ---- background ----

  canEnterBackgroundEditor() {
    const doc = this.doc;
    const userRole = this.userRole;
    return (
      (doc?.IsServerChart() && [kAdminUser].includes(userRole)) ||
      (!doc?.IsServerChart() &&
        this.canEditDocuments() &&
        this.hasFeature(enumFeaturesList.editBackgrounds))
    );
  }

  canAccessBackground() {
    return (
      [kAdminUser, kRWUser, kROUser].includes(this.userRole) &&
      this.hasFeature(enumFeaturesList.editBackgrounds)
    );
  }

  canModifyBackgrounds() {
    return this.canEnterBackgroundEditor() && Globals._uiConfig.fModifyBackgrounds;
  }

  // ---- chart / UI ----

  canAccessGrouping() {
    return (
      this.canEditDocuments() &&
      Globals._uiConfig.fShowGrouping &&
      this.hasFeature(enumFeaturesList.grouping)
    );
  }

  canAccessAbout() {
    return Globals._uiConfig.fEnableAbout;
  }

  canAccessInteractiveFields() {
    return (
      Globals._uiConfig.fInteractiveFields &&
      this.hasFeature(enumFeaturesList.interactiveFieldToggle)
    );
  }

  canAccessProfileCard() {
    return Globals._uiConfig.fHasProfile && this.hasFeature(enumFeaturesList.profile);
  }

  canAccessRecordDetails() {
    return this.canEditDocuments() && Globals._uiConfig.fRecordDetails;
  }

  canAccessFM() {
    return Globals._uiConfig.fCanFilemgr;
  }

  canAccessChartExplorer() {
    return Globals._uiConfig.fChartExplorer && this.hasFeature(enumFeaturesList.chartExplorer);
  }

  canAccessSubcharts() {
    return Globals._uiConfig.fHasSubcharts && this.hasFeature(enumFeaturesList.subcharts);
  }

  canAccessTalentPool() {
    return this.hasFeature(enumFeaturesList.talentPool);
  }

  canAccessChanges() {
    return this.hasFeature(enumFeaturesList.trackChanges);
  }

  canAccessPlanningChanges() {
    return this.hasFeature(enumFeaturesList.planChanges);
  }

  canAccessStaging() {
    return this.hasFeature(enumFeaturesList.staging);
  }

  canAccessAdvancedSearch() {
    return this.hasFeature(enumFeaturesList.advancedSearch);
  }

  canAccessWebLink() {
    return this.hasFeature(enumFeaturesList.exportWeblink);
  }

  canShareFilters() {
    return Globals._uiConfig.fShareFilters && this.canEditDocuments();
  }

  canExport() {
    return (
      (this.hasFeature(enumFeaturesList.exportPDFPPT) ||
        this.hasFeature(enumFeaturesList.exportExcel) ||
        this.hasFeature(enumFeaturesList.exportPNG)) &&
      Globals._uiConfig.fCanPublish
    );
  }

  canAutoExport() {
    return this.hasFeature(enumFeaturesList.autoExport);
  }

  // ---- file / folder access ----

  // Matches the pattern used in QOrgInfo.HandleViewerLaunchChartDocFallback
  // (line 319-324) and QXWSMgr.js:1857 — groupID "0" is the superuser/admin
  // bypass, otherwise the access group's folder list is the source of truth.
  canAccessFolder(dir) {
    if (!dir) return true;
    const userInfo = this.userInfo;
    const groupID = userInfo?.GetUserGroupID?.();
    if (groupID == "0") return true;
    const accessGroup = this.userGroup;
    if (!accessGroup) return false;
    return accessGroup.FolderInGroup(dir) !== enumFolderStatus.notingroup;
  }

  // Matches the pattern used in QXWSMgr.GetAllowedCharts (line 1864-1881).
  // groupID "0" = superuser/admin bypass; no fChartAccess map = all charts
  // allowed (default); explicit true required per chart otherwise.
  canAccessChart(chartId) {
    if (chartId == null) return true;
    const userInfo = this.userInfo;
    const groupID = userInfo?.GetUserGroupID?.();
    if (groupID == "0") return true;
    const accessGroup = this.userGroup;
    if (!accessGroup) return false;
    const chartAccess = accessGroup.fChartAccess;
    if (!chartAccess) return true;
    return chartAccess[chartId] === true;
  }

  // ---- branch-level security ----

  hasActiveBLS() {
    const userGroup = this.userGroup;
    const branchLevel = userGroup && userGroup.GetBranchLevelSecurity();
    const curChartID = this.doc?.GetChartID();
    const branchLevelProfile = branchLevel && branchLevel[curChartID];
    return branchLevelProfile != null && branchLevelProfile !== true;
  }

  // ---- plan-related ----

  canUpdatePlan() {
    return this.fWSMgr?.GetPlanMgr()?.CanUpdatePlan();
  }

  canAccessPlanApprovals() {
    return this.canAccessPlanning() && this.hasFeature(enumFeaturesList.planApprovals);
  }

  shouldShowPlanning() {
    return (
      this.canAccessPlanning() ||
      (this.showPricingAndPackaging() && !this.hasFeature(enumFeaturesList.planning))
    );
  }

  shouldShowPIT() {
    return (
      !!Globals._uiConfig?.fHasPIT &&
      this.canAccessPIT() &&
      (this.hasFeature(enumFeaturesList.PIT) ||
        (this.showPricingAndPackaging() && !this.hasFeature(enumFeaturesList.PIT)))
    );
  }

  hasSettingsMenu() {
    return (
      !!Globals._uiConfig?.fHasSettings &&
      (this.canAccessChartSettings() ||
        this.shouldShowPlanSettings() ||
        (this.canAccessTemplates() &&
          (this.canAccessViewLibrary() || this.showPricingAndPackaging())) ||
        this.canAccessAbout())
    );
  }

  shouldShowNavigation() {
    return !!Globals._uiConfig?.fHasNavigation;
  }

  shouldShowPlanSettings() {
    return this.isAdmin() && this.shouldShowPlanning();
  }

  shouldShowUpgradePlanning() {
    return (
      this.shouldShowPlanSettings() &&
      this.showPricingAndPackaging() &&
      !this.hasFeature(enumFeaturesList.planning)
    );
  }

  shouldShowPlanApprovals() {
    return (
      this.canAccessPlanApprovals() || (this.canAccessPlanning() && this.showPricingAndPackaging())
    );
  }

  // ---- pricing / packaging ----

  showPricingAndPackaging() {
    return Globals._uiConfig.fPackaging;
  }

  // ---- file manager ----

  fileManagerPermissions() {
    return this.fileMgr?.GetPermissions(this.userRole) ?? [];
  }
}
